/**
 * TldCards.tsx — populære domeneendelser som kort (som hos Domeneshop/Hostinger)
 *
 * Fire kort i rutenett: stor lett endelse, «Fra {pris} per år». Kortene er ikke
 * lenker — handlingen ligger i søkefeltet i heroen. Prisene kommer fra
 * domain-tlds.ts (tallpriser).
 */

import { formatPrice } from '@/lib/domain';
import type { DomainPopularContent, DomainTld } from '@/types/domain';
import SectionHeader from '@/components/product/SectionHeader';
import shared from '@/components/product/product.module.css';
import styles from './domain.module.css';

interface TldCardsProps {
  id: string;
  content: DomainPopularContent;
  items: DomainTld[];
  currency: string;
  /** Merketekst på det første kortet (f.eks. «Mest populære»); kortet får selv den fremhevede flaten (gradient + gitter), ingen pille */
  tag?: string;
}

export default function TldCards({ id, content, items, currency, tag }: TldCardsProps) {
  return (
    <section id={id} className={`${shared.sectionAlt} ${shared.net} ${shared.netJoin}`} aria-labelledby={`${id}-heading`}>
      <div className={shared.container}>
        <SectionHeader id={`${id}-heading`} eyebrow={content.eyebrow} heading={content.heading}>
          <p className={shared.lead}>{content.intro}</p>
        </SectionHeader>

        <ul className={styles.cards} role="list">
          {items.map((t, i) => (
            <li key={t.tld} className={`${styles.card} ${tag && i === 0 ? shared.featured : ''}`}>
              {/* Alle kort har en første rad; det fremhevede kortet (selve flaten er gradient + gitter) viser merketeksten der */}
              {tag && i === 0 ? (
                <p className={shared.featuredEyebrow}>{tag}</p>
              ) : (
                <span className={styles.cardSlot} aria-hidden="true" />
              )}
              <p className={styles.cardTld}>.{t.tld}</p>
              <p className={styles.cardPrice}>
                <span className={styles.cardFrom}>{content.priceFrom}</span>
                <span className={styles.cardAmount}>{formatPrice(t.register, currency)}</span>
                <span className={styles.cardPer}>{content.perYear}*</span>
              </p>
            </li>
          ))}
        </ul>
        <p className={styles.footnote}>{content.footnote}</p>
      </div>
    </section>
  );
}
